var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Column, Columns, EmptyState, FlexBox, HelpIcon, Spinner, Text, Tooltip, } from '@dixa/lib-shared-habla';
import { analytics } from 'app/utils/segment';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useShortHumanReadableTimer } from 'utils/useShortHumanReadableTimer';
import { useCurrentUser } from 'utils/user/useCurrentUser';
import { analyticsActions } from '../../analytics.actions';
import { AnalyticsControls } from '../../components/controls/analyticsControls';
import { AnalyticsHeader } from '../../components/header/analyticsHeader';
import { AnalyticsMenu } from '../../components/menu';
import { AnalyticsPanels } from '../../components/panels/analyticsPanels';
import { AnalyticsQuickViewFavoriteToggle } from '../../components/quickView/components/favorite/quickViewFavoriteToggle';
import { AnalyticsQuickViewModal } from '../../components/quickView/components/modal/quickViewModal';
import { useQuickViewActions } from './utils/useQuickViewActions/useQuickViewActions';
import { useQuickViewInit } from './utils/useQuickViewInit/useQuickViewInit';
export const QuickViewView = () => {
    const dispatch = useAppDispatch();
    const navigate = useAppNavigate();
    const { user } = useCurrentUser();
    const { quickView, asyncStatus } = useQuickViewInit();
    const { handleUpdateQuickView, handleDuplicateQuickView, handleDeleteQuickView, handleUpdateQuickViewFavoriteState, } = useQuickViewActions({ location: 'quickView' });
    const dashboard = useAppSelector((state) => state.analytics.dashboard);
    const meta = useAppSelector((state) => state.analytics.meta);
    /* The relative time of the last time the quick view data was refreshed. */
    const lastRefreshTime = useShortHumanReadableTimer(meta.lastRefresh);
    /** Whether the user can modify the current quick view. */
    const canModify = (quickView === null || quickView === void 0 ? void 0 : quickView.user.id) === user.id;
    /* The list of actions available for the current quick view. */
    const actions = [
        {
            type: 'action',
            icon: 'pen',
            label: 'Edit view',
            disabled: !canModify,
            tooltip: !canModify
                ? 'You can only edit quick views that are created by you.'
                : undefined,
            onClick: () => quickView && handleUpdateQuickView(quickView),
        },
        {
            type: 'action',
            icon: 'copy',
            label: 'Duplicate view',
            onClick: () => quickView && handleDuplicateQuickView(quickView),
        },
        {
            type: 'divider',
        },
        {
            type: 'action',
            icon: 'trash',
            label: 'Delete view',
            disabled: !canModify,
            tooltip: !canModify
                ? 'You can only delete quick views that are created by you.'
                : undefined,
            onClick: () => __awaiter(void 0, void 0, void 0, function* () {
                if (quickView) {
                    const { isDone } = yield handleDeleteQuickView(quickView);
                    isDone && navigate({ path: '/analytics/quick-views' });
                }
            }),
        },
        {
            type: 'divider',
        },
        {
            type: 'action',
            icon: 'redo',
            label: 'Refresh data',
            description: `Last refreshed ${lastRefreshTime}`,
            disabled: false,
            onClick: () => {
                dispatch(analyticsActions.setMeta({
                    meta: { lastRefresh: new Date().toISOString() },
                }));
                if (dashboard) {
                    analytics.analyticsRefreshClicked({ dashboard: dashboard.title });
                }
            },
        },
    ];
    return (_jsx(Columns, { children: _jsxs(Column, { children: [_jsxs(Column.Row, Object.assign({ slottable: true, borderBottom: true }, { children: [_jsx(Column.Slot, { children: _jsx(AnalyticsHeader, { children: quickView && (_jsxs(FlexBox, Object.assign({ gap: "200", alignItems: "center" }, { children: [_jsx(Text.Heading, Object.assign({ color: "primary" }, { children: quickView.name })), quickView.description && (_jsx(HelpIcon, { text: quickView.description, dark: true })), _jsx(Tooltip, Object.assign({ content: quickView.favorited
                                                ? 'Remove from the sidebar'
                                                : 'Add to the sidebar', dark: true }, { children: _jsx(AnalyticsQuickViewFavoriteToggle, { favorited: quickView.favorited, onClick: () => handleUpdateQuickViewFavoriteState(quickView) }) }))] }))) }) }), _jsx(Column.Slot, Object.assign({ noVerticalPadding: true, placement: "end" }, { children: _jsx(AnalyticsMenu, { actions: actions, direction: "left" }) }))] })), _jsxs(Column.Row, Object.assign({ size: 1, scrollable: true }, { children: [(asyncStatus === 'uninitialized' || asyncStatus === 'pending') && (_jsx(FlexBox, Object.assign({ height: "100%", alignItems: "center", justifyContent: "center" }, { children: _jsx(Spinner, {}) }))), asyncStatus === 'rejected' && (_jsx(EmptyState, { title: "Something went wrong", description: "We're experiencing some issues, try again or reload the page. If this issue persists, please contact support.", icon: "exclamation", iconColor: "red.500" })), asyncStatus === 'fulfilled' && (_jsxs(_Fragment, { children: [_jsxs(FlexBox, Object.assign({ flexDirection: "column", gap: "400" }, { children: [_jsx(AnalyticsControls, {}), _jsx(AnalyticsPanels, {})] })), _jsx(AnalyticsQuickViewModal, {})] }))] }))] }) }));
};
